/*
 * Updated ImageGallery Component - Grid Layout
 * Removed slick carousel dependency, implemented grid-based layout
 */

import React, {useMemo, useState, useEffect} from 'react'
import PropTypes from 'prop-types'

import {
    AspectRatio,
    Box,
    Img,
    Flex,
    Skeleton as ChakraSkeleton,
    GridItem,
    Grid,
    Text,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {ChevronLeftIcon, ChevronRightIcon} from '../../components/icons'
import {EnlargeIcon, VideoControlIcon} from '../../components/icons'
import {addDisParameter} from '../../utils/image-utils'

const LARGE = 'large'

/**
 * The skeleton representation of the image gallery component.
 */
export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})
    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Box>
                <AspectRatio ratio={1} {...styles.heroImageSkeleton} mb={4}>
                    <ChakraSkeleton />
                </AspectRatio>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                    {new Array(4).fill(0).map((_, index) => (
                        <AspectRatio ratio={1} {...styles.thumbnailImageSkeleton} key={index}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    ))}
                </Grid>
            </Box>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool
}

/**
 * Lightbox component for enlarged image viewing
 */
const Lightbox = ({isOpen, onClose, images, currentIndex, setCurrentIndex}) => {
    const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0)
    const thumbnailsPerView = useBreakpointValue({
        base: 3,
        sm: 5,
        md: 6,
        lg: 8,
        xl: 10
    })

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
    }

    const handleNext = () => {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
    }

    const handleThumbnailPrevious = () => {
        setThumbnailStartIndex((prev) => Math.max(0, prev - thumbnailsPerView))
    }

    const handleThumbnailNext = () => {
        setThumbnailStartIndex((prev) =>
            Math.min(images.length - thumbnailsPerView, prev + thumbnailsPerView)
        )
    }

    const handleThumbnailClick = (index) => {
        setCurrentIndex(index)
    }

    const productStyles = useMultiStyleConfig('ProductView')

    const handleKeyPress = (e) => {
        if (e.key === 'ArrowLeft') handlePrevious()
        if (e.key === 'ArrowRight') handleNext()
        if (e.key === 'Escape') onClose()
    }

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyPress)
            return () => document.removeEventListener('keydown', handleKeyPress)
        }
    }, [isOpen, currentIndex])

    // Auto-scroll thumbnails to keep current image visible
    useEffect(() => {
        if (images.length > thumbnailsPerView) {
            if (currentIndex < thumbnailStartIndex) {
                setThumbnailStartIndex(
                    Math.max(0, currentIndex - Math.floor(thumbnailsPerView / 2))
                )
            } else if (currentIndex >= thumbnailStartIndex + thumbnailsPerView) {
                setThumbnailStartIndex(
                    Math.min(
                        images.length - thumbnailsPerView,
                        currentIndex - Math.floor(thumbnailsPerView / 2)
                    )
                )
            }
        }
    }, [currentIndex, images.length])

    if (!images || images.length === 0) return null

    const visibleThumbnails = images.slice(
        thumbnailStartIndex,
        thumbnailStartIndex + thumbnailsPerView
    )

    const vimeoID = images[currentIndex]?.title?.match(/vimeo\.com\/(\d+)/)?.[1]

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
            <ModalOverlay bg="blackAlpha.900" />
            <ModalContent bg="transparent" boxShadow="none" maxH={'100%'}>
                <ModalCloseButton
                    aria-label="Modal close button"
                    {...productStyles.lightBoxCloseIcon}
                    sx={{
                        svg: {
                            width: '26px',
                            height: '26px',
                            strokeWidth: 4
                        }
                    }}
                    title="Modal close button"
                />
                <ModalBody {...productStyles.lightBoxBody} onClick={onClose}>
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        w="full"
                        h="full"
                        data-testid={'lightbox-main-image'}
                        display={'flex'}
                        height={{base: '55vh', sm: '55vh', md: '75vh', lg: '72vh'}}
                        {...(vimeoID
                            ? productStyles.lightBoxVideo
                            : {
                                  maxW: {
                                      base: '96vw',
                                      sm: '93vw',
                                      md: '46vw',
                                      lg: '33vw'
                                  }
                              })}
                    >
                        {vimeoID ? (
                            <iframe
                                width="100%"
                                height="100%"
                                title={images[currentIndex]?.alt || 'Product Video'}
                                src={`https://player.vimeo.com/video/${vimeoID}`}
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        ) : (
                            <Img
                                src={addDisParameter(
                                    images[currentIndex]?.disBaseLink ||
                                        images[currentIndex]?.link ||
                                        images[currentIndex],
                                    730
                                )}
                                alt={images[currentIndex]?.alt || 'Product image'}
                                maxW="100%"
                                maxH="100%"
                                objectFit="contain"
                                mx="auto"
                            />
                        )}

                        {images.length > 1 && (
                            <>
                                <IconButton
                                    icon={
                                        <ChevronLeftIcon
                                            boxSize={{base: '44px', sm: '48px', md: '64px'}}
                                        />
                                    }
                                    left={{base: '4', sm: '6', md: '8'}}
                                    onClick={handlePrevious}
                                    aria-label="Previous image"
                                    {...productStyles.lightBoxChevronIconLarge}
                                    title="Previous image"
                                />
                                <IconButton
                                    icon={
                                        <ChevronRightIcon
                                            boxSize={{base: '44px', sm: '48px', md: '64px'}}
                                        />
                                    }
                                    right={{base: '4', sm: '6', md: '8'}}
                                    onClick={handleNext}
                                    aria-label="Next image"
                                    {...productStyles.lightBoxChevronIconLarge}
                                    title="Next image"
                                />
                            </>
                        )}
                    </Box>

                    {/* Thumbnails Section */}
                    {images.length > 1 && (
                        <Box
                            data-testid={'lightbox-thumb-section'}
                            {...productStyles.lightBoxThumbnailSection}
                        >
                            {/* Previous thumbnails button */}
                            {images.length > thumbnailsPerView && thumbnailStartIndex > 0 && (
                                <IconButton
                                    icon={<ChevronLeftIcon boxSize="28px" />}
                                    left={{base: '4', sm: '6', md: '8'}}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleThumbnailPrevious()
                                    }}
                                    {...productStyles.lightBoxChevronIconSmall}
                                />
                            )}

                            {/* Thumbnails container */}
                            <Flex
                                data-testid={'lightbox-thumb-container'}
                                {...productStyles.lightBoxThumbnailContainer}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {visibleThumbnails.map((image, index) => {
                                    const actualIndex = thumbnailStartIndex + index
                                    const isActive = actualIndex === currentIndex

                                    return (
                                        <Box
                                            key={actualIndex}
                                            onClick={() => handleThumbnailClick(actualIndex)}
                                            border={
                                                isActive
                                                    ? '4px solid #FF640A'
                                                    : '4px solid transparent'
                                            }
                                            {...productStyles.lightBoxThumbnail}
                                        >
                                            <Img
                                                src={addDisParameter(
                                                    image?.disBaseLink || image?.link || image,
                                                    120
                                                )}
                                                alt={image?.alt || `Thumbnail ${actualIndex + 1}`}
                                                {...productStyles.lightBoxImage}
                                            />
                                        </Box>
                                    )
                                })}
                            </Flex>

                            {/* Next thumbnails button */}
                            {images.length > thumbnailsPerView &&
                                thumbnailStartIndex + thumbnailsPerView < images.length && (
                                    <IconButton
                                        icon={<ChevronRightIcon boxSize="28px" />}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleThumbnailNext()
                                        }}
                                        right={{base: '4', sm: '6', md: '8'}}
                                        {...productStyles.lightBoxChevronIconSmall}
                                    />
                                )}
                        </Box>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

// PropTypes for Lightbox component
Lightbox.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                disBaseLink: PropTypes.string,
                link: PropTypes.string,
                alt: PropTypes.string,
                title: PropTypes.string
            })
        ])
    ).isRequired,
    currentIndex: PropTypes.number.isRequired,
    setCurrentIndex: PropTypes.func.isRequired
}

/*
 * Mobile carousel component for thumbnail navigation - includes all images
 */
const MobileCarousel = ({images, onImageClick, currentIndex = 0}) => {
    const [startIndex, setStartIndex] = useState(0)
    const [touchStart, setTouchStart] = useState(null)
    const [touchEnd, setTouchEnd] = useState(null)
    const [calculatedHeight, setCalculatedHeight] = useState('auto')
    const visibleCount = 1

    const canScrollLeft = startIndex > 0
    const canScrollRight = startIndex + visibleCount < images.length

    const productStyles = useMultiStyleConfig('ProductView')

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50

    const scrollLeft = () => {
        if (canScrollLeft) {
            setStartIndex((prev) => Math.max(0, prev - 1))
        }
    }

    const scrollRight = () => {
        if (canScrollRight) {
            setStartIndex((prev) => Math.min(images.length - visibleCount, prev + 1))
        }
    }

    // Touch event handlers
    const onTouchStart = (e) => {
        setTouchEnd(null) // Reset touchEnd
        setTouchStart(e.targetTouches[0].clientX)
    }

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX)
    }

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return

        const distance = touchStart - touchEnd
        const isLeftSwipe = distance > minSwipeDistance
        const isRightSwipe = distance < -minSwipeDistance

        if (isLeftSwipe && canScrollRight) {
            scrollRight()
        }
        if (isRightSwipe && canScrollLeft) {
            scrollLeft()
        }
    }

    const handleImageLoad = (e) => {
        const img = e.target
        const containerWidth = img.parentElement?.offsetWidth || window.innerWidth * 0.9
        setCalculatedHeight(`${containerWidth}px`)
    }

    // Auto-scroll to show current image
    useEffect(() => {
        if (currentIndex < startIndex || currentIndex >= startIndex + visibleCount) {
            setStartIndex(Math.max(0, Math.min(currentIndex, images.length - visibleCount)))
        }
    }, [currentIndex, images.length])

    const visibleImages = images.slice(startIndex, startIndex + visibleCount)

    return (
        <Box position="relative" data-testid={'mobile-images-carousel'}>
            <Flex
                alignItems="center"
                bgColor={'white'}
                justifyContent="center"
                borderRadius="8px"
                minH={{base: '43vh', sm: 'auto'}}
            >
                <Flex
                    flex={1}
                    justifyContent="center"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    style={{touchAction: 'pan-y'}} // Allow vertical scrolling but handle horizontal swipes
                >
                    {visibleImages.map((image, index) => {
                        const actualIndex = startIndex + index
                        return (
                            <Box
                                key={actualIndex}
                                onClick={() => onImageClick(actualIndex)}
                                {...productStyles.mobileCarouselBox}
                            >
                                <Img
                                    src={image.disBaseLink || image?.link || image}
                                    alt={image?.alt || 'Product thumbnail'}
                                    draggable={false}
                                    onLoad={handleImageLoad}
                                    maxHeight={calculatedHeight}
                                    margin={'auto'}
                                />
                                <IconButton
                                    data-testid="enlarge-btn"
                                    aria-label="Enlarge image"
                                    icon={<EnlargeIcon />}
                                    {...productStyles.enlargeIcon}
                                />
                            </Box>
                        )
                    })}
                </Flex>
            </Flex>

            {/* Dots indicator */}
            {images.length > visibleCount && (
                <Box {...productStyles.dotsIndicatorContainer}>
                    <Flex {...productStyles.dotsIndicator}>
                        {Array.from({length: Math.ceil(images.length / visibleCount)}).map(
                            (_, index) => {
                                const isActive = Math.floor(startIndex / visibleCount) === index

                                return (
                                    <Box
                                        key={index}
                                        w={isActive ? '12px' : '8px'}
                                        h={isActive ? '12px' : '8px'}
                                        borderRadius="full"
                                        bg={isActive ? 'orangeBaseColor' : 'gray.100'}
                                        cursor="pointer"
                                        onClick={() => setStartIndex(index * visibleCount)}
                                        transition="all 0.2s"
                                    />
                                )
                            }
                        )}
                    </Flex>
                </Box>
            )}
        </Box>
    )
}

// PropTypes for MobileCarousel component
MobileCarousel.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                disBaseLink: PropTypes.string,
                link: PropTypes.string,
                alt: PropTypes.string,
                title: PropTypes.string
            })
        ])
    ).isRequired,
    onImageClick: PropTypes.func.isRequired,
    currentIndex: PropTypes.number
}

/**
 * Main ImageGallery component with grid layout
 */
const ImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    lazy = false,
    isQuickview
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const {isOpen, onOpen, onClose} = useDisclosure()

    // Responsive breakpoints
    const isMobile = useBreakpointValue({base: true, sm: false})

    const productStyles = useMultiStyleConfig('ProductView')

    // Get the 'hero' image for the current variation
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    const heroImages = heroImageGroup?.images || []
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    // Handle lightbox opening
    const openLightbox = (index = 0) => {
        setCurrentImageIndex(index)
        onOpen()
    }

    // Handle thumbnail click - always open lightbox from clicked image
    const handleThumbnailClick = (index) => {
        if (isMobile) {
            setCurrentImageIndex(index)
            openLightbox(index)
        } else {
            // For desktop, open lightbox directly without changing hero
            openLightbox(index)
        }
    }

    // Calculate image count category
    const imageCountCategory = useMemo(() => {
        if (heroImages.length === 1) return 'single'
        if (heroImages.length <= 3) return 'few'
        return 'many'
    }, [heroImages.length])

    /* Calculate dynamic styles based on image count and conditions
       for Tablet view of image section these calculation is required
    */
    const containerStyles = useMemo(() => {
        const baseStyles = {
            width: '100%'
        }

        if (isQuickview) {
            return baseStyles
        }

        if (imageCountCategory === 'many') {
            return {
                ...baseStyles,
                width: {base: '100%', sm: '113%', md: '100%'}
            }
        }

        return baseStyles
    }, [imageCountCategory, isQuickview])

    const heroContainerStyles = useMemo(() => {
        const baseStyles = {
            position: 'relative',
            mb: 4
        }

        if (imageCountCategory === 'single') {
            return {
                ...baseStyles,
                float: 'none',
                width: '100%',
                mb: 0
            }
        }

        if (imageCountCategory === 'few') {
            return {
                ...baseStyles,
                float: {base: 'none', sm: 'left', md: 'none'},
                width: {base: '100%', sm: '60%', md: '100%'}
            }
        }

        return {
            ...baseStyles,
            float: {base: 'none', sm: 'left', md: 'none'},
            width: {base: '100%', sm: '46.7%', md: '100%'}
        }
    }, [imageCountCategory])

    const gridStyles = useMemo(() => {
        const baseGridStyles = {
            gap: 4,
            maxW: '100%'
        }

        if (heroImages.length <= 1) {
            return {
                ...baseGridStyles,
                display: 'none'
            }
        }

        // Modified for 'few' images case
        if (imageCountCategory === 'few') {
            return {
                ...baseGridStyles,
                display: 'grid',
                gridTemplateColumns: {
                    base: 'repeat(2, 1fr)',
                    sm: 'repeat(1, 1fr)',
                    md: 'repeat(2, 1fr)'
                },
                gridTemplateRows: '1fr',
                gridAutoFlow: {base: 'row', sm: 'row', md: 'row'},
                float: {base: 'none', sm: 'left', md: 'none'},
                width: {base: '100%', sm: '29%', md: '100%'}
            }
        }

        // many images
        return {
            ...baseGridStyles,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gridAutoFlow: {base: 'row', sm: 'column', md: 'row'},
            float: {base: 'none', sm: 'left', md: 'none'},
            width: {base: '100%', sm: '48.7%', md: '100%'}
        }
    }, [heroImages.length, imageCountCategory])

    //return if no images
    if (heroImages.length === 0) {
        return null
    }
    const vimeoID = heroImages[0]?.title?.match(/vimeo\.com\/(\d+)/)?.[1]

    return (
        <Box
            className="product-hero-image-section"
            data-testid="image-gallery-container"
            sx={containerStyles}
        >
            {isMobile || isQuickview ? (
                // Mobile: Show carousel with all images, no separate hero image
                <Box>
                    <MobileCarousel
                        images={heroImages}
                        onImageClick={handleThumbnailClick}
                        currentIndex={currentImageIndex}
                    />
                </Box>
            ) : (
                // Desktop/Tablet: Show hero image + thumbnail grid
                <>
                    {/* Hero Image 1st large image*/}
                    <Box data-testid="hero-container" sx={heroContainerStyles}>
                        {heroImages[0] && (
                            <Box
                                position="relative"
                                cursor="pointer"
                                bgColor="white"
                                borderRadius="8px"
                                onClick={() => openLightbox(0)}
                                height={{base: 'auto', sm: '424px', lg: '640px'}}
                                display="flex"
                            >
                                {vimeoID ? (
                                    <Box {...productStyles.heroVideoBlock}>
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            title={heroImages[0]?.alt || 'Product Video'}
                                            src={`https://player.vimeo.com/video/${vimeoID}`}
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </Box>
                                ) : (
                                    <Img
                                        src={addDisParameter(
                                            heroImages[0].disBaseLink ||
                                                heroImages[0]?.link ||
                                                heroImages[0],
                                            640
                                        )}
                                        alt={heroImages[0]?.alt || 'Product image'}
                                        loading={loadingStrategy}
                                        margin="auto"
                                        borderRadius="8px"
                                    />
                                )}
                                <IconButton
                                    data-testid="enlarge-btn"
                                    aria-label="Enlarge image"
                                    icon={<EnlargeIcon />}
                                    {...productStyles.enlargeIcon}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Thumbnail Grid - Desktop/Tablet: 2 columns, max 4 thumbnails */}
                    {heroImages.length > 1 && (
                        <Box data-testid="ipad-container-layout-change">
                            <Grid
                                data-testid="thumnail-grid"
                                sx={gridStyles}
                                ml={{base: '0', sm: '4', md: '0'}}
                            >
                                {/* Show thumbnails - if more than 4 images, show first 3 thumbnails normally and last one with overlay */}
                                {heroImages.length > 4
                                    ? /* Case 1: More than 4 images - show first 2 thumbnails normally */
                                      heroImages.slice(1, 4).map((image, index) => {
                                          const actualIndex = index + 1 // Add 1 because we start from index 1
                                          const isVideoTile =
                                              image?.title?.match(/vimeo\.com\/(\d+)/)?.[1]

                                          return (
                                              <GridItem key={actualIndex}>
                                                  <Box
                                                      onClick={() =>
                                                          handleThumbnailClick(actualIndex)
                                                      }
                                                      {...productStyles.gridItemBox}
                                                      tabIndex={'0'}
                                                      onKeyDown={(e) => {
                                                          if (e.key === 'Enter' || e.key === ' ') {
                                                              e.preventDefault()
                                                              handleThumbnailClick(actualIndex)
                                                          }
                                                      }}
                                                  >
                                                      <AspectRatio ratio={1}>
                                                          <Img
                                                              src={
                                                                  image.disBaseLink
                                                                      ? `${image.disBaseLink}?sw=310&sh=310`
                                                                      : image?.link || image
                                                              }
                                                              alt={
                                                                  image?.alt || 'Product thumbnail'
                                                              }
                                                              loading={loadingStrategy}
                                                              objectFit="cover"
                                                          />
                                                      </AspectRatio>
                                                      {isVideoTile && (
                                                          <VideoControlIcon
                                                              position="absolute"
                                                              bottom="1rem"
                                                              left="1rem"
                                                          />
                                                      )}
                                                  </Box>
                                              </GridItem>
                                          )
                                      })
                                    : /* Case 2: 4 or fewer images - show all remaining thumbnails normally */
                                      heroImages.slice(1).map((image, index) => {
                                          const actualIndex = index + 1 // Add 1 because we start from index 1
                                          const isVideoTile =
                                              image?.title?.match(/vimeo\.com\/(\d+)/)?.[1]
                                          return (
                                              <GridItem key={actualIndex}>
                                                  <Box
                                                      position="relative"
                                                      cursor="pointer"
                                                      borderRadius="8px"
                                                      overflow="hidden"
                                                      onClick={() =>
                                                          handleThumbnailClick(actualIndex)
                                                      }
                                                      tabIndex={'0'}
                                                      onKeyDown={(e) => {
                                                          if (e.key === 'Enter' || e.key === ' ') {
                                                              e.preventDefault()
                                                              handleThumbnailClick(actualIndex)
                                                          }
                                                      }}
                                                  >
                                                      <AspectRatio ratio={1}>
                                                          <Img
                                                              src={
                                                                  image.disBaseLink ||
                                                                  image?.link ||
                                                                  image
                                                              }
                                                              alt={
                                                                  image?.alt || 'Product thumbnail'
                                                              }
                                                              loading={loadingStrategy}
                                                              objectFit="cover"
                                                          />
                                                      </AspectRatio>
                                                      {isVideoTile && (
                                                          <VideoControlIcon
                                                              position="absolute"
                                                              bottom="1rem"
                                                              left="1rem"
                                                          />
                                                      )}
                                                  </Box>
                                              </GridItem>
                                          )
                                      })}

                                {/* Last thumbnail - show normally if exactly 5 images */}
                                {heroImages.length === 5 && (
                                    <GridItem>
                                        <Box
                                            position="relative"
                                            cursor="pointer"
                                            borderRadius="lg"
                                            overflow="hidden"
                                            _hover={{borderColor: 'orangeBaseColor'}}
                                            onClick={() => handleThumbnailClick(4)} // Opens the 5th image (index 4)
                                            tabIndex={'0'}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    handleThumbnailClick(4)
                                                }
                                            }}
                                        >
                                            <AspectRatio ratio={1}>
                                                <Img
                                                    src={
                                                        heroImages[4].disBaseLink ||
                                                        heroImages[4]?.link ||
                                                        heroImages[4]
                                                    }
                                                    alt={heroImages[4]?.alt || 'Product thumbnail'}
                                                    loading={loadingStrategy}
                                                    objectFit="cover"
                                                />
                                            </AspectRatio>
                                        </Box>
                                    </GridItem>
                                )}

                                {/* Last thumbnail with +X overlay for more than 5 images */}
                                {heroImages.length > 5 && (
                                    <GridItem>
                                        <Box
                                            onClick={() => handleThumbnailClick(4)} // Opens the 5th image (index 4)
                                            {...productStyles.xMoreGridItemBox}
                                            tabIndex={'0'}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    handleThumbnailClick(4)
                                                }
                                            }}
                                        >
                                            <AspectRatio ratio={1}>
                                                <Img
                                                    src={
                                                        heroImages[4].disBaseLink ||
                                                        heroImages[4]?.link ||
                                                        heroImages[4]
                                                    }
                                                    alt={heroImages[4]?.alt || 'Product thumbnail'}
                                                    loading={loadingStrategy}
                                                    objectFit="cover"
                                                />
                                            </AspectRatio>
                                            {/* Overlay with +X more */}
                                            <Box {...productStyles.xMoreOverlay}>
                                                <Text {...productStyles.xMoreOverlayNumber}>
                                                    +{heroImages.length - 5}
                                                </Text>
                                            </Box>
                                        </Box>
                                    </GridItem>
                                )}
                            </Grid>
                        </Box>
                    )}
                </>
            )}
            {/* Lightbox */}
            <Lightbox
                isOpen={isOpen}
                onClose={onClose}
                images={heroImages}
                currentIndex={currentImageIndex}
                setCurrentIndex={setCurrentImageIndex}
            />
        </Box>
    )
}

ImageGallery.propTypes = {
    imageGroups: PropTypes.array,
    bundleChildProducts: PropTypes.array,
    selectedVariationAttributes: PropTypes.object,
    size: PropTypes.string,
    lazy: PropTypes.bool,
    isProductABundle: PropTypes.bool,
    isSingleVariantTile: PropTypes.bool,
    isQuickview: PropTypes.bool
}

export default ImageGallery
